import { Button } from "antd";
import moment from "moment";
import React, { forwardRef, Fragment, useRef, useState, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import getSetting, { getLogoUrl } from "../../../api/getSettings";
import bookLogo from "../../../assets/images/bookLogo.png";
import "../../Invoice/style.css";

const PrintToPdf = forwardRef(({ order, invoiceData }, ref) => {
  // Calculate totals
  const subtotal = order?.order_items?.reduce((sum, item) => {
    const itemTotal = item.sale_price * item.ordered_quantity;
    const itemDiscount = (itemTotal * (item.discount || 0)) / 100;
    return sum + (itemTotal - itemDiscount);
  }, 0) || 0;

  const billDiscountPercent = order?.discount || 0;
  const billDiscountAmount = (subtotal * billDiscountPercent) / 100;
  const totalAmount = subtotal - billDiscountAmount;

  return (
    <Fragment>
      <div
        ref={ref}
        className="wrapper"
        data-order-number={order?.order_number}
        data-order-date={moment(order?.order_date).format("DD-MMM-YYYY")}
        style={{ position: 'relative', paddingBottom: '200px' }}
      >
        {/* Company Detail */}
        <div className="box1">
          <img src={getLogoUrl(invoiceData?.logo) || bookLogo} alt="Company Logo" style={{ maxWidth: "100px", maxHeight: "100px" }} />
        </div>
        <div className="box2">
          <h3>
            <b>{invoiceData?.company_name}</b>
          </h3>
          <p>{invoiceData?.address}</p>
          <p>Phone: {invoiceData?.phone}</p>
          <p>Email: {invoiceData?.email}</p>
        </div>

        <div className="box4">
          <hr className="hr1" />
          <h3>SALES ORDER</h3>
          <hr className="hr1" />
          <p>
            <b>{invoiceData?.tag_line}</b>
          </p>
        </div>

        {/* Customer Detail */}
        <div className="box5">
          <table className="table2">
            <tr>
              <th>
                <b>Customer</b>
              </th>
              <td>{order?.customer?.name || "-"}</td>
            </tr>
            {order?.customer?.address && (
              <tr>
                <th>Address</th>
                <td>{order.customer.address}</td>
              </tr>
            )}
            {order?.customer?.phone && (
              <tr>
                <th>Contact</th>
                <td>{order.customer.phone}</td>
              </tr>
            )}
          </table>
        </div>

        {/* Order Detail */}
        <div className="box6">
          <table className="table2">
            <tr>
              <th>Order No</th>
              <td>{order?.order_number}</td>
            </tr>
            <tr>
              <th>Order Date</th>
              <td>{moment(order?.order_date).format("DD-MMM-YYYY")}</td>
            </tr>
            <tr>
              <th>Status</th>
              <td>{order?.status?.toUpperCase() || "-"}</td>
            </tr>
            {order?.user?.username && (
              <tr>
                <th>Sales Person</th>
                <td>{order.user.username}</td>
              </tr>
            )}
          </table>
        </div>

        {/* Product details */}
        <div className="box7">
          <table className="table1">
            <thead>
              <tr>
                <th>Sl</th>
                <th>ISBN</th>
                <th>Title</th>
                <th>Author</th>
                <th>Publisher</th>
                <th>Price</th>
                <th>Qty</th>
                <th>Disc %</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {order?.order_items?.map((item, index) => {
                const itemTotal = item.sale_price * item.ordered_quantity;
                const itemDiscount = (itemTotal * (item.discount || 0)) / 100;
                const itemAmount = itemTotal - itemDiscount;
                const billDisc = (itemAmount * billDiscountPercent) / 100;
                const finalItemAmount = itemAmount - billDisc;

                return (
                  <tr key={item.id}>
                    <td><p style={{ margin: 0 }}>{index + 1}</p></td>
                    <td><p style={{ margin: 0 }}>{item.product?.isbn || "-"}</p></td>
                    <td><p style={{ margin: 0 }}>{item.product?.name || "-"}</p></td>
                    <td><p style={{ margin: 0 }}>{item.product?.author || "-"}</p></td>
                    <td><p style={{ margin: 0 }}>{item.product?.book_publisher?.name || "N/A"}</p></td>
                    <td><p style={{ margin: 0 }}>₹{parseFloat(item.sale_price || 0).toFixed(2)}</p></td>
                    <td><p style={{ margin: 0 }}>{item.ordered_quantity}</p></td>
                    <td><p style={{ margin: 0 }}>{item.discount || 0}%</p></td>
                    <td style={{ whiteSpace: 'nowrap' }}><p style={{ margin: 0 }}>₹{finalItemAmount.toFixed(2)}</p></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="box9">
          <table className="table2">
            <tr>
              <th>Subtotal</th>
              <td>₹{subtotal.toFixed(2)}</td>
            </tr>
            {billDiscountPercent > 0 && (
              <tr>
                <th>Bill Discount ({billDiscountPercent}%)</th>
                <td>₹{billDiscountAmount.toFixed(2)}</td>
              </tr>
            )}
            <tr>
              <th>
                <b>Total</b>
              </th>
              <td>
                <b>₹{totalAmount.toFixed(2)}</b>
              </td>
            </tr>
          </table>
        </div>

        {/* Note */}
        {order?.note && (
          <div className="box11">
            <p>
              <b>Note:</b> {order.note}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="box12">
          <p>{invoiceData?.footer_text || "Thank you for your business!"}</p>
        </div>
      </div>
    </Fragment>
  );
});

PrintToPdf.displayName = "PrintToPdf";

const SalesOrderPrint = ({ order, autoShow = false }) => {
  const componentRef = useRef();
  const [showPdf, setShowPDF] = useState(autoShow);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const [invoiceData, setInvoiceData] = useState(null);
  useEffect(() => {
    getSetting()
      .then((data) => {
        setInvoiceData(data?.result);
      })
      .catch((error) => {
        console.error("Error fetching invoice settings:", error);
      });
  }, []);

  // Auto-show PDF on mobile
  useEffect(() => {
    if (autoShow && invoiceData) {
      setShowPDF(true);
    }
  }, [autoShow, invoiceData]);

  const showPrint = () => {
    setShowPDF(true);
  };

  const hideShowPrint = () => {
    setShowPDF(false);
  };

  if (!order) {
    return null;
  }

  return (
    <div>
      {showPdf && (
        <div className="popUp">
          <div
            style={{
              position: "relative",
              backgroundColor: "white",
              margin: "2% 5%",
            }}
          >
            {/* Mobile Back Button */}
            <div className="d-lg-none mb-3">
              <Button 
                type="default" 
                onClick={() => hideShowPrint()}
                style={{ marginBottom: '16px' }}
              >
                ← Back to Details
              </Button>
            </div>
            
            <PrintToPdf
              ref={componentRef}
              order={order}
              invoiceData={invoiceData}
            />
            {invoiceData && (
              <Button
                type="primary"
                shape="round"
                onClick={() => {
                  handlePrint();
                  hideShowPrint();
                }}
                style={{ marginTop: "20px" }}
              >
                Print PDF
              </Button>
            )}
          </div>
        </div>
      )}
      {!showPdf && invoiceData && (
        <Button type="primary" shape="round" onClick={showPrint}>
          Print PDF
        </Button>
      )}
    </div>
  );
};

export default SalesOrderPrint;
